import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AnimatedSection } from '../ui/AnimatedSection';
import { ExternalLink } from 'lucide-react';

export function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const discordLogo = '/images/discord-mark-white.svg';
  const tiktokLogo = '/images/TikTok-Social-Icon-Mono-White.svg';
  const kaizenLogo = '/images/kaizen-network-white-horizontal.svg';

  return (
    <footer className="relative bg-gray-900 py-16">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/0 to-black/30" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Logo and Social Media Section */}
          <AnimatedSection animation="fade-up" className="flex flex-col items-center">
            <Link to="/" className="transition-transform hover:scale-105">
              <img
                src={kaizenLogo}
                alt="Kaizen Network"
                className="h-20 w-auto max-w-[280px] object-contain"
              />
            </Link>
            <div className="flex justify-center w-full space-x-8 mt-6">
              <a
                href="https://dsc.gg/kaizenmc"
                target="_blank"
                rel="noopener noreferrer"
                className="transform transition-all hover:scale-110 hover:opacity-80"
                aria-label={t('footer.joinDiscord')}
              >
                <img
                  src={discordLogo}
                  alt="Discord"
                  className="h-8 w-8"
                />
              </a>
              <a
                href="https://www.tiktok.com/@kaizenetwork"
                target="_blank"
                rel="noopener noreferrer"
                className="transform transition-all hover:scale-110 hover:opacity-80"
                aria-label={t('footer.followTikTok')}
              >
                <img
                  src={tiktokLogo}
                  alt="TikTok"
                  className="h-8 w-8"
                />
              </a>
            </div>
          </AnimatedSection>

          {/* About Section */}
          <AnimatedSection animation="fade-up" className="text-gray-400" delay>
            <h3 className="text-white text-lg font-semibold mb-4">{t('footer.aboutUs')}</h3>
            <p className="text-sm leading-relaxed">
              {t('footer.aboutDescription')}
            </p>
          </AnimatedSection>

          {/* Useful Links Section */}
          <AnimatedSection animation="fade-up" className="text-gray-400" delay>
            <h3 className="text-white text-lg font-semibold mb-4">{t('footer.usefulLinks')}</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link 
                  to="/vote" 
                  className="hover:text-white transition-colors"
                >
                  {t('navbar.vote')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/leaderboard" 
                  className="hover:text-white transition-colors"
                >
                  {t('nav.leaderboard')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/store"
                  className="hover:text-white transition-colors"
                >
                  {t('navbar.store')}
                </Link>
              </li>
              <li>
                <a 
                  href="https://status.kaizenmc.id"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors flex items-center"
                >
                  {t('footer.status')}
                  <ExternalLink className="w-3 h-3 ml-1 text-gray-400" />
                </a>
              </li>
              <li>
                <a
                  href="https://kaizenmc.fandom.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors flex items-center"
                >
                  {t('nav.wiki')}
                  <ExternalLink className="w-3 h-3 ml-1 text-gray-400" />
                </a>
              </li>
            </ul>
          </AnimatedSection>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
          <AnimatedSection animation="fade-up" delay>
            <p>{t('footer.copyright', { year: currentYear })}</p>
          </AnimatedSection>
        </div>
      </div>
    </footer>
  );
} 